import React, { useState, useRef } from "react";
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  Popover,
  Modal,
} from "@mui/material";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import Picker from "emoji-picker-react";
import QRCode from "react-qr-code";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import html2canvas from "html2canvas";

const steps = [
  "Just feed your WhatsApp Business number into VertexSuite WhatsApp QR Code generator",
  "Add an alluring and engaging message to your WhatsApp Code to make it more interactive",
  "Once ready, hit the ‘Generate Link’ button to get your unique, free WhatsApp QR Code",
];

const QRCodeGenerator = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmoji, setSelectedEmoji] = useState("");
  const [qrValue, setQrValue] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const qrRef = useRef(null);

  // Emoji Picker Handlers
  const handleEmojiClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setAnchorEl(null);
  };

  const handleEmojiSelect = (emojiObject) => {
    setSelectedEmoji(emojiObject.emoji);
    setMessage((prev) => prev + emojiObject.emoji);
    handleEmojiClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? "emoji-popover" : undefined;

  const previewWhatsapp = () => {
    const cleanedPhone = phoneNumber.replace(/\D/g, "");
    const encodedMessage = encodeURIComponent(message);
    const whatsappLink = `https://api.whatsapp.com/send?phone=${cleanedPhone}&text=${encodedMessage}`;
    window.open(whatsappLink, "_blank");
  };

  const generateWhatsAppQR = () => {
    const cleanedPhone = phoneNumber.replace(/\D/g, "");
    const encodedMessage = encodeURIComponent(message);
    const whatsappLink = `https://wa.me/${cleanedPhone}${message ? `?text=${encodedMessage}` : ""}`;
    setQrValue(whatsappLink);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const downloadQRCode = () => {
    if (qrRef.current) {
      html2canvas(qrRef.current, { backgroundColor: "#ffffff" }).then((canvas) => {
        const link = document.createElement("a");
        link.download = "whatsapp-qr-code.png";
        link.href = canvas.toDataURL("image/png");
        link.click();
      });
    }
  };

  return (
    <Box
      sx={{
        backgroundImage: `url('bg3.svg')`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        width: "100%",
        py: 6,
        display: "flex",
        alignItems: "center",
      }}
    >
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Form Section */}
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} sm={8} md={6}>
            <Box
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                p: 4,
                backgroundColor: "#FFF",
                boxShadow: 2,
                minHeight: "480px",
                height: "100%",
              }}
            >
              {/* WhatsApp Number */}
              <Typography variant="subtitle1" sx={{ mb: 1, color: "#333" }}>
                Enter your WhatsApp number
              </Typography>

              <PhoneInput
                country={"in"}
                value={phoneNumber}
                onChange={setPhoneNumber}
                placeholder="Your phone number"
                inputStyle={{
                  width: "100%",
                  height: "56px",
                }}
                dropdownStyle={{
                  zIndex: 10000,
                }}
              />

              <Typography
                variant="caption"
                sx={{ color: "#666", mt: 1, display: "block" }}
              >
                Note: Check if the country code you have entered is correct.
              </Typography>

              {/* Custom Message */}
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, mt: 3, color: "#333" }}
              >
                Add your custom message
              </Typography>

              <TextField
                fullWidth
                placeholder="Add custom message that users will send to you"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton onClick={handleEmojiClick}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            backgroundColor: "#FFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          😊
                        </Box>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Example: 'Hello, I am interested to know more about your business.'"
              />

              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleEmojiClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <Picker onEmojiClick={handleEmojiSelect} />
              </Popover>

              {/* Buttons Section */}
              <Box sx={{ mt: 4 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: "#E0E0E0",
                    borderRadius: "18px",
                    color: "#666",
                    textTransform: "none",
                    mb: 2,
                    height: "50px",
                    fontSize: "16px",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#1976D2",
                      color: "#FFF",
                      borderColor: "#1976D2",
                    },
                  }}
                  onClick={previewWhatsapp}
                  disabled={!phoneNumber}
                >
                  Preview
                </Button>

                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    color: "#666",
                    mb: 2,
                    fontSize: "18px",
                  }}
                >
                  - or -
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "rgb(0, 209, 132)",
                    borderRadius: "18px",
                    textTransform: "none",
                    height: "50px",
                    fontSize: "16px",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#00897B",
                    },
                  }}
                  onClick={generateWhatsAppQR}
                  disabled={!phoneNumber}
                >
                  Generate my link
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Step Section */}
          <Grid item xs={12} sm={8} md={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: "bold",
                  mb: 2,
                  color: "rgb(255,255,255)",
                }}
              >
                Create QR Codes for{" "}
                <span style={{ color: "#00d084" }}>WhatsApp Business</span> in 3
                simple steps
              </Typography>
              {steps.map((step, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    backgroundColor: "white",
                    borderRadius: "10px",
                    p: 4,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "1rem", md: "1.2rem" },
                      color: "#00d084",
                    }}
                  >
                    Step {index + 1} 👉
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "1rem", md: "1.2rem" },
                      color: "text.secondary",
                    }}
                  >
                    {step}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Modal for QR Code Preview */}
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="qr-code-modal-title"
          aria-describedby="qr-code-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 400 },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            <Typography id="qr-code-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
              Your WhatsApp QR Code
            </Typography>
            <Box ref={qrRef} sx={{ mb: 2, p: 2, bgcolor: "#ffffff" }}>
              <QRCode
                value={qrValue}
                size={256}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </Box>
            <Typography sx={{ mb: 2 }}>
              Scan this QR code to start a WhatsApp chat!
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button
                variant="contained"
                onClick={downloadQRCode}
                sx={{
                  backgroundColor: "rgb(0, 209, 132)",
                  "&:hover": { backgroundColor: "#00897B" },
                }}
              >
                Download PNG
              </Button>
              <Button
                variant="outlined"
                onClick={handleCloseModal}
                sx={{
                  borderColor: "#E0E0E0",
                  color: "#666",
                  "&:hover": { borderColor: "#1976D2", color: "#1976D2" },
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      </Container>
    </Box>
  );
};

export default QRCodeGenerator;